import React, { Fragment, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import config from '../../../../config';

import ReportShell from '../shared/ReportShell';
import ReportFilterBar from '../shared/ReportFilterBar';
import ReportTable, { formatCount } from '../shared/ReportTable';
import ReportPagination from '../shared/ReportPagination';
import ReportExportButtons from '../shared/ReportExportButtons';
import ColumnPicker from '../shared/ColumnPicker';
import useColumnConfig from '../shared/useColumnConfig';
import useReportLookups from '../shared/useReportLookups';
import useReportAccess from '../shared/useReportAccess';

const PAGE_SIZE = 50;

const yesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
};
const TODAY = new Date().toISOString().split('T')[0];

const ALL_COLUMNS = [
    { key: 'tenant_store_name', label: 'Tenant Name' },
    { key: 'tenant_group_name', label: 'Group Name' },
    { key: 'tenant_code', label: 'Tenant Code' },
    { key: 'tenant_unit_code', label: 'Unit Code' },
    { key: 'property_name', label: 'Property' },
    { key: 'subcategory_name', label: 'Subcategory' },
    { key: 'floor', label: 'Floor' },
    { key: 'total_transaction_count', label: 'Transaction Count', align: 'right', format: formatCount, isCount: true, total: true },
    { key: 'total_net_sales', label: 'Net Sales', isMoney: true, total: true },
    { key: 'live_days', label: 'Live Days', align: 'right', format: formatCount, isCount: true },
];

const DEFAULT_COLUMNS = ALL_COLUMNS.map((c) => c.key);

const initialFilters = {
    startDate: yesterday(),
    endDate: yesterday(),
    property_id: '',
    selectedFloor: '',
    category_id: '',
    subcategory_id: '',
    group_name: '',
    selectedTenants: [],
};

const DaywiseConsolidatedView = () => {
    const { t } = useTranslation();
    const allowed = useReportAccess();

    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [filters, setFilters] = useState(initialFilters);
    const {
        selectedColumns, onChange: onColumnsChange, columnOptions,
        visibleColumns, exportColumns, totalKeys,
    } = useColumnConfig(ALL_COLUMNS, DEFAULT_COLUMNS);
    const [reportData, setReportData] = useState([]);
    const [hasRun, setHasRun] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState({ key: '', dir: 'asc' });

    const lookups = useReportLookups({
        propertyId: filters.property_id,
        categoryId: filters.category_id,
        subcategoryId: filters.subcategory_id,
        groupName: filters.group_name,
    });
    const { concessionaireId, propertyAccessNames } = lookups;

    const onFilterChange = (key, value) => {
        setFilters((prev) => {
            const next = { ...prev, [key]: value };
            if (key === 'category_id') { next.subcategory_id = ''; next.selectedTenants = []; }
            if (key === 'property_id') { next.selectedFloor = ''; next.selectedTenants = []; }
            if (key === 'subcategory_id') { next.selectedTenants = []; }
            if (key === 'group_name') { next.selectedTenants = []; }
            return next;
        });
        setReportData([]);
        setHasRun(false);
        setPage(0);
    };

    const resetFilters = () => {
        setFilters(initialFilters);
        setReportData([]);
        setHasRun(false);
        setPage(0);
        setError('');
        setSort({ key: '', dir: 'asc' });
    };

    const insertToLog = (task) => {
        axios.post(`${config.hostname}/log`, {
            created_by: login_id,
            concessionaire_id: concessionaireId,
            module: 'Reports',
            task: `${login_name}${task}`,
        }, { headers }).catch((err) => console.warn('audit log failed', err?.response?.status));
    };

    const nameOf = (opts, val) => opts.find((o) => o.value === val)?.label || '';

    const generateReport = async () => {
        setLoading(true);
        setError('');
        setPage(0);
        const payload = {
            concessionaire_id: concessionaireId,
            property_access: propertyAccessNames,
            start_date: filters.startDate,
            end_date: filters.endDate,
            group_name: filters.group_name,
            category_name: nameOf(lookups.categoryOptions, filters.category_id),
            property_name: nameOf(lookups.propertyOptions, filters.property_id),
            subcategory_name: nameOf(lookups.subcategoryOptions, filters.subcategory_id),
            tenant_id: filters.selectedTenants,
            floor: filters.selectedFloor,
        };
        try {
            const res = await axios.post(`${config.hostname}/reports/getdaywiseconsolidatedreport`, payload, { headers });
            setReportData(Array.isArray(res.data) ? res.data : []);
            setHasRun(true);
            insertToLog(' generated Sales Summary report');
        } catch (err) {
            console.error('sales summary report failed', err);
            setError(err?.response?.data?.message || t('The report request failed. Please adjust your filters and try again.'));
            toast.error(t('Failed to generate Sales Summary report'));
        } finally {
            setLoading(false);
        }
    };

    const sortedData = useMemo(() => {
        const data = [...reportData];
        if (!sort.key) return data;
        data.sort((a, b) => {
            const av = a[sort.key]; const bv = b[sort.key];
            if (av < bv) return sort.dir === 'asc' ? -1 : 1;
            if (av > bv) return sort.dir === 'asc' ? 1 : -1;
            return 0;
        });
        return data;
    }, [reportData, sort]);

    const pageCount = Math.ceil(sortedData.length / PAGE_SIZE);
    const pageRows = sortedData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const requestSort = (key) => {
        setSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
    };

    const totals = useMemo(() => {
        const t = {};
        totalKeys.forEach((k) => {
            if (visibleColumns.some((c) => c.key === k)) {
                t[k] = reportData.reduce((s, i) => s + (Number(i[k]) || 0), 0);
            }
        });
        return Object.keys(t).length ? t : null;
    }, [reportData, visibleColumns, totalKeys]);

    const fields = [
        { key: 'startDate', label: t('Start date'), type: 'date', role: 'startDate', max: TODAY },
        { key: 'endDate', label: t('End date'), type: 'date', role: 'endDate', max: TODAY },
        { key: 'property_id', label: t('Property'), type: 'select', options: lookups.propertyOptions, placeholder: t('All properties') },
        { key: 'selectedFloor', label: t('Floor'), type: 'select', options: lookups.floorOptions, placeholder: t('All floors') },
        { key: 'category_id', label: t('Category'), type: 'select', options: lookups.categoryOptions, placeholder: t('All categories') },
        { key: 'subcategory_id', label: t('Sub category'), type: 'select', options: lookups.subcategoryOptions, placeholder: t('All sub categories') },
        { key: 'group_name', label: t('Group'), type: 'select', options: lookups.groupOptions, placeholder: t('All groups') },
        { key: 'selectedTenants', label: t('Tenants'), type: 'multiselect', options: lookups.tenantOptions, placeholder: t('All tenants'), span: 2 },
    ];

    const exportMetadata = useMemo(() => {
        const dates = filters.startDate === filters.endDate
            ? [[t('Date'), filters.startDate]]
            : [[t('From'), filters.startDate], [t('To'), filters.endDate]];
        return [
            ...dates,
            [t('Property'), nameOf(lookups.propertyOptions, filters.property_id) || t('All')],
            [t('Category'), nameOf(lookups.categoryOptions, filters.category_id) || t('All')],
            [t('Group'), filters.group_name || t('All')],
            [t('Subcategory'), nameOf(lookups.subcategoryOptions, filters.subcategory_id) || t('All')],
            [t('Floor'), filters.selectedFloor || t('All')],
            [t('Tenants'), filters.selectedTenants.length ? t('{{count}} selected', { count: filters.selectedTenants.length }) : t('All')],
            [t('Exported By'), login_name],
            [t('Exported On'), new Date().toLocaleString()],
        ];
    }, [filters, lookups, login_name, t]);

    if (!allowed) return null;

    return (
        <Fragment>
            <ReportShell
                title={t('Sales Summary Report')}
                subtitle={t('Consolidated daywise transaction counts, net sales and live days per tenant.')}
                actions={
                    <ReportExportButtons
                        heading={t('Sales Summary Report')}
                        metadata={exportMetadata}
                        columns={exportColumns}
                        data={sortedData}
                        fileName="SalesSummaryReport"
                        landscape
                        onAfterExport={(fmt) => insertToLog(` exported Sales Summary report to ${fmt}`)}
                    />
                }
            >
                <ReportFilterBar
                    fields={fields}
                    values={filters}
                    onChange={onFilterChange}
                    onGenerate={generateReport}
                    onReset={resetFilters}
                    loading={loading}
                />

                <ColumnPicker
                    options={columnOptions}
                    value={selectedColumns}
                    total={ALL_COLUMNS.length}
                    onChange={onColumnsChange}
                />

                <ReportTable
                    columns={visibleColumns}
                    rows={pageRows}
                    sort={sort}
                    onSort={requestSort}
                    totals={totals}
                    loading={loading}
                    error={error}
                    emptyMessage={hasRun
                        ? t('No data found for the selected filters. Try widening the date range or clearing filters.')
                        : t('Choose your filters and select Generate report to view results.')}
                />

                <ReportPagination
                    page={page}
                    pageCount={pageCount}
                    onChange={setPage}
                    totalItems={sortedData.length}
                    pageSize={PAGE_SIZE}
                    itemLabel={t('rows')}
                />
            </ReportShell>
        </Fragment>
    );
};

export default DaywiseConsolidatedView;
